/**
 * Aba "Perfil" da ficha: o que as conversas dizem do cliente.
 *
 * 1. Como o cliente está: satisfação, temperamento e exigência, medidos pelo
 *    Jev nas mensagens do cliente e da família (palavra, posição na escala,
 *    confiança e tendência; cor só quando piora).
 * 2. Perfil: o texto do Gemini por partes, cada item com "De onde veio"
 *    (as mensagens que o sustentam) e o botão "Escrever perfil", que gasta 1
 *    dos pedidos do dia.
 * 3. Quem é quem: cada pessoa das conversas com o papel e de onde ele veio
 *    (regra, IA ou correção à mão); quem trata a Central corrige o papel.
 *
 * Só aparece para quem lê as mensagens: o servidor manda `perfil: null` aos outros.
 */
import React, { useState } from "react";
import { FiCheck, FiChevronDown, FiEdit3 } from "react-icons/fi";
import styled from "styled-components";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { corrigirPapelDoParticipante, escreverPerfil } from "../../../services/centralProjetos";
import { BP_MOBILE } from "../../../styles/canonical/breakpoints";
import { formatarDataLocal } from "../../../utils/dinheiro";
import { iniciais } from "../apresentacao";
import { MensagensDeOrigem } from "../comum";
import { Ancora, Avatar, BotaoCompacto, OpcaoDoMenu, Popover, RotuloDoPopover } from "../conversas/estilos";
import usePopover from "../conversas/usePopover";
import { avisoDoPedido } from "../gemini";
import { Item, Lista, Secao, SecaoTitulo, Vazio } from "../styles";
import useGeminiHoje from "../useGeminiHoje";
import {
  PAPEIS,
  leiturasDasEscalas,
  ordenarParticipantes,
  origemDoParticipante,
  rotuloDoPapel,
  secoesDoTexto,
  textoDePoucaConversa,
} from "./leituraDoPerfil";

const CELULAR = `(max-width: ${BP_MOBILE}px)`;

/* ---------- Escalas ---------- */

const LinhaDaEscala = styled.div`
  display: grid;
  grid-template-columns: 120px minmax(0, 240px) minmax(140px, 220px);
  justify-content: start;
  gap: 4px 20px;
  align-items: center;
  padding: 12px 14px;

  > span:first-child {
    font-size: 12.5px;
    color: var(--ink-3);
  }

  @media ${CELULAR} {
    grid-template-columns: minmax(0, 1fr);
    gap: 6px;
  }
`;

const LeituraDaEscala = styled.div`
  display: grid;
  gap: 2px;
  min-width: 0;

  b {
    font-size: 14.5px;
    font-weight: 600;
    color: var(--ink);
  }

  small {
    font-size: 12px;
    color: var(--ink-3);
  }
`;

const Tendencia = styled.span`
  color: ${({ $tom }) => ($tom === "warning" ? "var(--warning-text)" : "inherit")};
  font-weight: ${({ $tom }) => ($tom === "warning" ? 600 : 400)};
`;

/* Posição na escala: um ponto no trilho (não é "mais é melhor"). */
const Trilho = styled.span`
  position: relative;
  display: block;
  height: 4px;
  margin: 0 5px;
  border-radius: 2px;
  background: var(--surface-3);

  > span {
    position: absolute;
    top: 50%;
    width: 10px;
    height: 10px;
    margin: -5px 0 0 -5px;
    border-radius: 50%;
    background: var(--ink-2);
    box-shadow: 0 0 0 2px var(--surface);
  }
`;

function PosicaoNaEscala({ leitura }) {
  return (
    <Trilho
      role="meter"
      aria-label={leitura.rotulo}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(leitura.nota * 100)}
      aria-valuetext={leitura.palavra}
    >
      <span style={{ left: `${leitura.nota * 100}%` }} />
    </Trilho>
  );
}

function Escalas({ escalas, em }) {
  const leituras = leiturasDasEscalas(escalas);
  let conteudo;
  if (!escalas) conteudo = <Vazio>Ainda sem avaliação: o processo de fundo avalia uma vez por dia quando chega mensagem do cliente.</Vazio>;
  else if (escalas.insuficiente) conteudo = <Vazio>{textoDePoucaConversa(escalas)}</Vazio>;
  else if (!leituras.length) conteudo = <Vazio>A avaliação veio sem as escalas.</Vazio>;
  else {
    conteudo = leituras.map((leitura) => (
      <Item key={leitura.chave}>
        <LinhaDaEscala aria-label={leitura.rotulo}>
          <span>{leitura.rotulo}</span>
          <LeituraDaEscala>
            <b>{leitura.palavra}</b>
            <small>
              {leitura.confianca}
              {leitura.confianca && leitura.tendencia ? " · " : ""}
              {leitura.tendencia ? <Tendencia $tom={leitura.tendencia.tom}>{leitura.tendencia.texto}</Tendencia> : null}
            </small>
          </LeituraDaEscala>
          <PosicaoNaEscala leitura={leitura} />
        </LinhaDaEscala>
      </Item>
    ));
  }
  return (
    <Secao aria-labelledby="perfil-escalas">
      <SecaoTitulo>
        <h2 id="perfil-escalas">Como o cliente está</h2>
        <span>{em ? `avaliado em ${formatarDataLocal(em)}, pelas mensagens do cliente e da família` : "pelas mensagens do cliente e da família"}</span>
      </SecaoTitulo>
      <Lista>{conteudo}</Lista>
    </Secao>
  );
}

/* ---------- Texto do perfil ---------- */

const ParteDoTexto = styled.div`
  display: grid;
  gap: 6px;
  padding: 12px 14px;

  h3 {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-3);
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 8px;
  }

  p {
    margin: 0;
    max-width: 72ch;
    font-size: 14px;
    line-height: 1.55;
    color: var(--ink);
    text-wrap: pretty;
  }

  /* "De onde veio" fica colado ao texto, à esquerda. */
  li > button {
    margin-left: -6px;
  }
`;

const Pedir = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;

  > span {
    flex: 1 1 220px;
    min-width: 0;
    font-size: 12px;
    line-height: 1.45;
    color: ${({ $esgotado }) => ($esgotado ? "var(--warning-text)" : "var(--ink-3)")};
  }

  > p {
    flex: 1 1 100%;
    margin: 0;
    font-size: 12.5px;
    color: var(--danger-text);
  }
`;

function EscreverPerfil({ pedido, jaEscrito, aoEscrito }) {
  const { request } = useAuth();
  const { gemini, recarregar } = useGeminiHoje();
  const [erro, setErro] = useState("");
  const aviso = avisoDoPedido(gemini, "reserva");

  async function escrever() {
    setErro("");
    try {
      await escreverPerfil(request, pedido);
    } catch (falha) {
      setErro(falha?.message || "O perfil não saiu agora.");
      throw falha;
    } finally {
      recarregar();
    }
    aoEscrito();
  }

  return (
    <Pedir $esgotado={aviso.esgotado}>
      <AsyncButton
        variant="secondary"
        disabled={aviso.esgotado}
        title={aviso.texto}
        successMessage="Perfil escrito."
        silentError
        onClick={escrever}
      >
        <FiEdit3 aria-hidden="true" />
        {jaEscrito ? "Escrever de novo" : "Escrever perfil"}
      </AsyncButton>
      <span>{aviso.texto}</span>
      {erro ? <p role="alert">{erro}</p> : null}
    </Pedir>
  );
}

function TextoDoPerfil({ pedido, texto, em, podeEscrever, aoMudar }) {
  const secoes = secoesDoTexto(texto);
  return (
    <Secao aria-labelledby="perfil-texto">
      <SecaoTitulo>
        <h2 id="perfil-texto">Perfil</h2>
        <span>{em ? `escrito em ${formatarDataLocal(em)}, a partir das conversas` : "a partir das conversas"}</span>
      </SecaoTitulo>
      <Lista>
        {secoes.length === 0 ? (
          <Vazio>Perfil ainda não escrito. A IA escreve na rodada semanal, ou agora pelo botão abaixo.</Vazio>
        ) : null}
        {secoes.map((secao) => (
          <Item key={secao.chave}>
            <ParteDoTexto aria-labelledby={`perfil-${secao.chave}`}>
              <h3 id={`perfil-${secao.chave}`}>{secao.rotulo}</h3>
              <ul>
                {secao.itens.map((item, i) => (
                  // O texto não tem id próprio e não muda de ordem enquanto a tela está aberta.
                  <li key={i}>
                    <p>{item.texto}</p>
                    {item.mensagens.length ? (
                      <MensagensDeOrigem
                        ids={item.mensagens}
                        discreto
                        rotulo={item.mensagens.length === 1 ? "De onde veio" : `De onde veio (${item.mensagens.length})`}
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            </ParteDoTexto>
          </Item>
        ))}
      </Lista>
      {podeEscrever ? <EscreverPerfil pedido={pedido} jaEscrito={secoes.length > 0} aoEscrito={aoMudar} /> : null}
    </Secao>
  );
}

/* ---------- Quem é quem ---------- */

const LinhaDaPessoa = styled.div`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) auto;
  gap: 2px 12px;
  align-items: center;
  padding: 10px 12px 10px 14px;

  > div {
    display: grid;
    gap: 1px;
    min-width: 0;
  }

  b {
    font-size: 14px;
    font-weight: 600;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  small {
    font-size: 12.5px;
    color: var(--ink-3);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media ${CELULAR} {
    .rotulo {
      display: none;
    }
  }
`;

const PapelSemDono = styled.span`
  color: var(--warning-text);
  font-weight: 600;
`;

/* O menu do papel sai por baixo da última linha (a lista padrão corta o que transborda). */
const ListaDePessoas = styled(Lista)`
  overflow: visible;
`;

/* Corrigir é ação de exceção: discreta até passar o mouse. */
const BotaoCorrigir = styled(BotaoCompacto)`
  border-color: transparent;
  background: transparent;
  color: var(--ink-2);

  &:hover:not(:disabled),
  &[aria-expanded="true"] {
    border-color: var(--border);
    background: var(--surface);
    color: var(--ink);
  }
`;

/* O menu abre embaixo do botão, alinhado à direita (dentro da tela também no celular). */
const MenuDoPapel = styled(Popover)`
  @media ${CELULAR} {
    position: absolute;
    top: calc(100% + 6px);
    left: auto;
    right: 0;
    width: 260px;
    max-height: none;
    overflow: visible;
  }
`;

function CorrigirPapel({ pedido, participante, aoMudar }) {
  const { request } = useAuth();
  const toast = useToast();
  const popover = usePopover();
  const [ocupado, setOcupado] = useState(false);

  async function escolher(papel) {
    if (papel === participante.papel) {
      popover.fechar(true);
      return;
    }
    setOcupado(true);
    try {
      await corrigirPapelDoParticipante(request, pedido, participante.contato_id, papel);
    } catch {
      // O toast do erro já saiu pelo `request`; o menu fica aberto para tentar de novo.
      setOcupado(false);
      return;
    }
    setOcupado(false);
    popover.fechar(true);
    toast.success(`${participante.nome || "Contato"} agora é ${PAPEIS.find((p) => p.valor === papel)?.rotulo || papel}.`);
    aoMudar();
  }

  return (
    <Ancora ref={popover.ancora}>
      <BotaoCorrigir
        ref={popover.gatilho}
        type="button"
        aria-haspopup="menu"
        aria-expanded={popover.aberto}
        aria-label={`Corrigir o papel de ${participante.nome || "contato"}`}
        title="Corrigir o papel"
        onClick={popover.alternar}
      >
        <FiEdit3 size={14} aria-hidden="true" />
        <span className="rotulo">Corrigir</span>
        <FiChevronDown size={14} aria-hidden="true" className="rotulo" />
      </BotaoCorrigir>
      {popover.aberto ? (
        <MenuDoPapel $largura={260} role="menu" aria-label={`Papel de ${participante.nome || "contato"}`}>
          <RotuloDoPopover>Quem é {participante.nome || "esta pessoa"}?</RotuloDoPopover>
          {PAPEIS.map((papel) => {
            const atual = papel.valor === participante.papel;
            return (
              <OpcaoDoMenu
                key={papel.valor}
                type="button"
                role="menuitemradio"
                aria-checked={atual}
                disabled={ocupado}
                onClick={() => escolher(papel.valor)}
              >
                {atual ? <FiCheck size={16} aria-hidden="true" /> : <span aria-hidden="true" />}
                <b>{papel.rotulo}</b>
                <small>{papel.ajuda}</small>
              </OpcaoDoMenu>
            );
          })}
        </MenuDoPapel>
      ) : null}
    </Ancora>
  );
}

function Participantes({ pedido, participantes, podeTratar, aoMudar }) {
  const pessoas = ordenarParticipantes(participantes);
  return (
    <Secao aria-labelledby="perfil-pessoas">
      <SecaoTitulo>
        <h2 id="perfil-pessoas">Quem é quem nas conversas</h2>
        <span>{pessoas.length || ""}</span>
      </SecaoTitulo>
      <ListaDePessoas>
        {pessoas.length === 0 ? <Vazio>Ninguém identificado nas conversas ainda.</Vazio> : null}
        {pessoas.map((pessoa) => {
          const papel = rotuloDoPapel(pessoa);
          const origem = origemDoParticipante(pessoa);
          return (
            <Item key={pessoa.contato_id}>
              <LinhaDaPessoa>
                <Avatar $tamanho={32} aria-hidden="true">{iniciais(pessoa.nome)}</Avatar>
                <div>
                  <b title={pessoa.nome}>{pessoa.nome || "Sem nome"}</b>
                  <small>
                    {pessoa.papel === "nao_identificado" || !pessoa.papel ? <PapelSemDono>{papel}</PapelSemDono> : papel}
                    {origem ? ` · ${origem}` : ""}
                  </small>
                </div>
                {podeTratar ? <CorrigirPapel pedido={pedido} participante={pessoa} aoMudar={aoMudar} /> : <span />}
              </LinhaDaPessoa>
            </Item>
          );
        })}
      </ListaDePessoas>
    </Secao>
  );
}

/** `perfil`: `{participantes, escalas, texto, escalas_em, texto_em}` da ficha. */
export default function PerfilDoCliente({ pedido, perfil, podeTratar, podeEscrever, aoMudar }) {
  return (
    <>
      <Escalas escalas={perfil.escalas} em={perfil.escalas_em} />
      <TextoDoPerfil pedido={pedido} texto={perfil.texto} em={perfil.texto_em} podeEscrever={podeEscrever} aoMudar={aoMudar} />
      <Participantes pedido={pedido} participantes={perfil.participantes} podeTratar={podeTratar} aoMudar={aoMudar} />
    </>
  );
}
